<template>
  <el-dialog
    v-model="visible"
    :title="dialogTitle"
    width="450px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="review-modal-content">
      <template v-if="isRejected">
        <div class="rejection-reason-block">
          <div class="rejection-label">Rejection reason</div>
          <div class="rejection-text">
            {{ reasonText || 'No reason provided' }}
          </div>
          <a
            v-if="isStaff && reportUrl"
            :href="reportUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="report-link"
          >
            View full report →
          </a>
        </div>

        <div class="attempts-section">
          <div class="attempts-indicator">
            <span
              v-for="n in maxAttempts"
              :key="n"
              class="attempt-dot"
              :class="{
                used: n <= reviewAttemptsCount,
                'last-attempt': isLastAttempt && n === maxAttempts
              }"
            />
          </div>
          <span class="attempts-text">
            {{ attemptsRemainingText }}
          </span>
        </div>

        <div v-if="isLastAttempt" class="warning-block">
          If rejected again, this creative will be archived automatically.
        </div>

        <el-checkbox v-model="confirmed" class="confirm-checkbox">
          I've fixed the issue described above
        </el-checkbox>
      </template>

      <template v-else>
        <p class="confirm-text">
          Are you sure you want to send this creative to review?
        </p>
      </template>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">Cancel</el-button>
        <el-button
          type="primary"
          :disabled="isRejected && !confirmed"
          :loading="loading"
          @click="handleSubmit"
        >
          {{ isRejected ? 'Submit' : 'Send to review' }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import { fetchReviewInfo, IReviewInfo } from '@/apps/traforama/api/creatives'
import { useUserStore } from '@/store/modules/user'

const MAX_REVIEW_ATTEMPTS = 3

export default defineComponent({
  name: 'ReviewConfirmModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    creativeId: {
      type: String,
      required: true
    },
    creativeState: {
      type: String,
      default: 'new'
    }
  },
  emits: ['update:modelValue', 'confirm'],
  setup(props, { emit }) {
    const { isStaff } = useUserStore()
    const visible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })

    const loading = ref(false)
    const confirmed = ref(false)
    const reviewAttemptsCount = ref(0)
    const statusChangeReason = ref<string | null>(null)
    const reportUrl = ref<string | null>(null)
    const maxAttempts = MAX_REVIEW_ATTEMPTS

    const isRejected = computed(() => props.creativeState === 'rejected')

    const reasonText = computed(() => statusChangeReason.value || '')

    const dialogTitle = computed(() => {
      if (!isRejected.value) return 'Send to review?'
      return isLastAttempt.value
        ? 'Last review attempt'
        : 'Send to review again?'
    })

    const isLastAttempt = computed(() => {
      return reviewAttemptsCount.value === maxAttempts - 1
    })

    const attemptsRemainingText = computed(() => {
      const remaining = maxAttempts - reviewAttemptsCount.value
      if (remaining === 1) {
        return '1 attempt left'
      }
      return `${remaining} attempts remaining`
    })

    const fetchData = async () => {
      if (!props.creativeId) return

      loading.value = true
      try {
        const data: IReviewInfo = await fetchReviewInfo(props.creativeId)
        reviewAttemptsCount.value = data.review_attempts_count
        statusChangeReason.value = data.status_change_reason
        reportUrl.value = data.report_url
      } catch (error) {
        console.error('Failed to fetch review info:', error)
      } finally {
        loading.value = false
      }
    }

    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue) {
          confirmed.value = false
          if (isRejected.value) {
            fetchData()
          }
        }
      }
    )

    const handleClose = () => {
      visible.value = false
      confirmed.value = false
    }

    const handleSubmit = () => {
      if (isRejected.value && !confirmed.value) return
      emit('confirm')
      handleClose()
    }

    return {
      isStaff,
      visible,
      loading,
      confirmed,
      reviewAttemptsCount,
      statusChangeReason,
      reportUrl,
      reasonText,
      maxAttempts,
      isRejected,
      dialogTitle,
      isLastAttempt,
      attemptsRemainingText,
      handleClose,
      handleSubmit
    }
  }
})
</script>

<style lang="scss" scoped>
.review-modal-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rejection-reason-block {
  background-color: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 4px;
  padding: 12px;
}

.rejection-label {
  font-size: 12px;
  color: #f56c6c;
  margin-bottom: 4px;
}

.rejection-text {
  white-space: pre-line;
  font-size: 14px;
  color: #303133;
  word-break: break-word;
}

.report-link {
  display: inline-block;
  margin-top: 8px;
  color: #f56c6c;
  text-decoration: none;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
}

.attempts-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.attempts-indicator {
  display: flex;
  gap: 4px;
}

.attempt-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #dcdfe6;
  transition: background-color 0.2s;

  &.used {
    background-color: #f56c6c;
  }

  &.last-attempt {
    background-color: #e6a23c;
  }
}

.attempts-text {
  font-size: 13px;
  color: #606266;
}

.warning-block {
  background-color: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 4px;
  padding: 12px;
  color: #e6a23c;
  font-size: 13px;
}

.confirm-checkbox {
  margin-top: 8px;
}

.confirm-text {
  font-size: 14px;
  color: #606266;
  margin: 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
